import { h } from 'hyperapp'
import './app.css'
import Game from './Game'
import Title from './Title'
import Winner from './Winner'
import BestTime from './BestTime'
import CustomField from './CustomField'
import { REGION_ASSETS } from './regions'

const levels = {
  beginner: {
    level: 'beginner',
    width: 9,
    height: 9,
    mines: 10,
  },
  intermediate: {
    level: 'intermediate',
    width: 16,
    height: 16,
    mines: 40,
  },
  expert: {
    level: 'expert',
    width: 30,
    height: 16,
    mines: 99,
  },
}

const levelNames = Object.keys(levels)

/** First visit & invalid saves — 16×16, 40 mines (not beginner). */
const DEFAULT_LEVEL = levels.intermediate

/** Default region: Strait of Hormuz (`straight_of_hormuz.jpg`). */
const DEFAULT_REGION = 'hormuz'

const canUseDom = typeof window !== 'undefined'

/** Suppress duplicate mousedown from synthetic mouse events right after a touch. */
let lastTouchEndedAt = 0

/** Only the fields createGame needs — never pass a full `game` object (avoids stale field/raf). */
const pickGameParams = (src) => {
  if (
    src &&
    typeof src.level === 'string' &&
    Number.isFinite(src.width) &&
    Number.isFinite(src.height) &&
    Number.isFinite(src.mines)
  ) {
    return {
      level: src.level,
      width: src.width,
      height: src.height,
      mines: src.mines,
    }
  }
  return DEFAULT_LEVEL
}

const createGame = ({ level, width, height, mines }) => {
  const field = new Array(height)
  for (let y = 0, m = mines; y < height; y++) {
    field[y] = new Array(width)
    for (let x = 0; x < width; x++, m--) {
      field[y][x] = {
        opened: false,
        mined: m > 0,
        flag: false,
        mark: false,
        num: 0,
      }
    }
  }
  return {
    level,
    width,
    height,
    mines,
    field,
    left: width * height - mines,
    pressed: false,
    pressedX: -1,
    pressedY: -1,
    group: false,
    flags: mines,
    start: -1,
    time: 0,
    raf: null,
    lose: false,
    win: false,
    touch: false,
    hoverX: -1,
    hoverY: -1,
  }
}

const toggleFlagAt = (game, marks, x, y) => {
  if (x < 0 || y < 0) return null
  const cell = game.field[y][x]
  if (cell.opened) return null
  const nextField = game.field.slice()
  nextField[y] = nextField[y].slice()
  const nextCell = {
    ...cell,
    flag: marks ? !cell.mark && !cell.flag : !cell.flag,
    mark: marks && cell.flag,
  }
  nextField[y][x] = nextCell
  const mines = cell.flag === nextCell.flag ? game.flags : game.flags + (nextCell.flag ? -1 : 1)
  return {
    game: {
      ...game,
      field: nextField,
      flags: mines,
    },
  }
}

/** Chord (reveal adjacent) on an opened numbered cell — shared by mouse up and Space. */
const chordReveal = (game, px, py, actions) => {
  const { width, height } = game
  const field = game.field.map((row) => row.map((c) => ({ ...c })))
  const outBounds = (x, y) => x < 0 || y < 0 || x >= width || y >= height
  const calcNear = (x, y, fn) => {
    let num = 0
    for (let offsetX = -1; offsetX <= 1; offsetX++) {
      for (let offsetY = -1; offsetY <= 1; offsetY++) {
        if (!outBounds(offsetX + x, offsetY + y)) {
          num += fn(field[offsetY + y][offsetX + x]) ? 1 : 0
        }
      }
    }
    return num
  }
  let { left, lose } = game
  const reveal = (x, y) => {
    if (outBounds(x, y)) return 0
    const c = field[y][x]
    if (c.opened || c.flag) return 0
    c.opened = true
    c.num = calcNear(x, y, (i) => i.mined)
    if (c.mined) lose = true
    if (c.num) return 1
    return revealRange(x, y, reveal) + 1
  }
  const cell = field[py][px]
  if (!cell.opened) return null
  const flags = calcNear(px, py, (c) => c.flag)
  if (flags !== cell.num) return null
  left -= revealRange(px, py, reveal)
  const win = left <= 0
  if (win || lose) {
    cancelAnimationFrame(game.raf)
    if (!lose) actions.win()
    window.gtag('event', lose ? 'lose' : 'win', {
      event_category: 'game',
      event_label: game.level,
      value: game.time,
    })
  }
  return {
    game: {
      ...game,
      left,
      field,
      pressed: false,
      group: false,
      lose,
      win,
      touch: false,
    },
  }
}

const openable = (game, x, y) => {
  const cell = game.field[y][x]
  if (cell.opened) {
    let num = 0
    for (let offsetX = -1; offsetX <= 1; offsetX++) {
      for (let offsetY = -1; offsetY <= 1; offsetY++) {
        const dx = offsetX + x
        const dy = offsetY + y
        if (dx >= 0 && dy >= 0 && dx < game.width && dy < game.height) {
          num += game.field[dy][dx].flag ? 1 : 0
        }
      }
    }
    return num === cell.num
  }
  return false
}

const revealRange = (x, y, fn) =>
  fn(x - 1, y - 1) +
  fn(x - 1, y + 1) +
  fn(x + 1, y - 1) +
  fn(x + 1, y + 1) +
  fn(x - 1, y) +
  fn(x + 1, y) +
  fn(x, y - 1) +
  fn(x, y + 1)

const customLevel = (data, defaults) => {
  const width = Math.max(9, Math.min(40, parseInt(data.width, 10) || defaults.width))
  const height = Math.max(9, Math.min(30, parseInt(data.height, 10) || defaults.height))
  const cells = width * height
  const parsed = parseInt(data.mines, 10)
  const raw = Number.isFinite(parsed) && parsed >= 0 ? parsed : defaults.mines
  const maxMines = Math.max(1, cells - 1)
  return {
    level: 'custom',
    width,
    height,
    mines: Math.max(1, Math.min(maxMines, raw)),
  }
}

const loadLevel = () => {
  if (canUseDom) {
    let level = localStorage.getItem('level')
    if (level == null || level === '') {
      localStorage.setItem('level', DEFAULT_LEVEL.level)
      return DEFAULT_LEVEL
    }
    // Old installs / starter default used beginner; minimum default is now intermediate.
    if (level === 'beginner') {
      localStorage.setItem('level', 'intermediate')
      level = 'intermediate'
    }
    if (level === 'standard') {
      localStorage.setItem('level', 'intermediate')
      level = 'intermediate'
    }
    if (typeof level === 'string') {
      if (levels[level]) return levels[level]
      const data = level.split(',')
      return customLevel({ width: data[0], height: data[1], mines: data[2] }, DEFAULT_LEVEL)
    }
  }
  return DEFAULT_LEVEL
}

const loadRegion = () => {
  if (!canUseDom) return DEFAULT_REGION
  const id = localStorage.getItem('region')
  if (id == null || id === '' || !REGION_ASSETS[id]) {
    localStorage.setItem('region', DEFAULT_REGION)
    return DEFAULT_REGION
  }
  return id
}

const prevent = (event) => {
  event.preventDefault()
  return false
}

export const state = {
  destroy: false,
  menu: null,
  game: createGame(loadLevel()),
  region: loadRegion(),
  marks: canUseDom ? Boolean(localStorage.getItem('marks')) : false,
  winner: null,
  bestTime: null,
  customField: null,
}

export const actions = {
  getState: () => (state) => state,
  init: () => (state, actions) => {
    window.addEventListener('keydown', actions.press, true)
  },
  destroy: () => (state, actions) => {
    window.removeEventListener('keydown', actions.press, true)
    return { destroy: true }
  },
  press: (event) => (state, actions) => {
    const t = event.target
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return
    if (event.code === 'F2' || event.keyCode === 113 || event.code === 'KeyN') {
      actions.reset()
      return
    }
    if (event.code === 'Space' || event.key === ' ') {
      event.preventDefault()
      actions.space()
    }
  },
  space: () => ({ game, marks }, actions) => {
    if (game.win || game.lose) return null
    const { hoverX, hoverY } = game
    if (hoverX < 0 || hoverY < 0) return null
    const cell = game.field[hoverY][hoverX]
    if (!cell.opened) {
      return toggleFlagAt(game, marks, hoverX, hoverY)
    }
    if (game.start < 0) return null
    if (!openable(game, hoverX, hoverY)) return null
    return chordReveal(game, hoverX, hoverY, actions)
  },
  hover: ({ x, y }) => ({ game }) => ({
    game: { ...game, hoverX: x, hoverY: y },
  }),
  reset: (payload) => ({ game }) => {
    cancelAnimationFrame(game.raf)
    const raw = (payload && levels[payload.level]) || payload || game
    const params = pickGameParams(raw)
    window.gtag('event', params.level === game.level ? 'reset' : 'level', {
      event_category: 'game',
      event_label: params.level,
    })
    localStorage.setItem(
      'level',
      params.level === 'custom' ? [params.width, params.height, params.mines].join() : params.level,
    )
    return {
      menu: null,
      game: createGame(params),
    }
  },
  touch: ({ event, x, y }) => ({ game }, actions) => {
    if (game.win || game.lose) return null
    const rect = event.currentTarget.getBoundingClientRect()
    const closed = !game.field[y][x].opened
    const longPressMs = 500
    const timer = closed ? setTimeout(actions.flag, longPressMs) : null
    const move = ({ touches }) => {
      if (
        !touches ||
        touches.length !== 1 ||
        touches[0].clientX < rect.left ||
        touches[0].clientX > rect.right ||
        touches[0].clientY < rect.top ||
        touches[0].clientY > rect.bottom
      ) {
        if (timer) clearTimeout(timer)
        actions.move({ x: -1, y: -1 })
      }
    }
    const stop = ({ changedTouches }) => {
      if (timer) clearTimeout(timer)
      lastTouchEndedAt = Date.now()
      window.removeEventListener('scroll', move)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', stop)
      window.removeEventListener('touchcancel', stop)
      move({ touches: changedTouches })
      actions.up()
    }
    window.addEventListener('scroll', move)
    window.addEventListener('touchmove', move)
    window.addEventListener('touchend', stop)
    window.addEventListener('touchcancel', stop)
    return {
      game: {
        ...game,
        pressed: true,
        pressedX: x,
        pressedY: y,
        group: event.shiftKey || openable(game, x, y),
        touch: true,
      },
    }
  },
  flag: () => ({ game, marks }) => {
    const { pressedX, pressedY } = game
    const r = toggleFlagAt(game, marks, pressedX, pressedY)
    if (!r) return null
    return { game: { ...r.game, pressedX: -1, pressedY: -1 } }
  },
  down: ({ event, x, y }) => ({ game, marks }, actions) => {
    if (game.touch || event.button > 2 || game.win || game.lose) return null
    if (Date.now() - lastTouchEndedAt < 450) return null
    window.addEventListener('mouseup', actions.up)
    let { field, flags } = game
    const cell = field[y][x]
    const ctrlFlag = event.button === 0 && event.ctrlKey && !cell.opened
    // Right-click: always cycle flag / mark on closed cells (do not require !game.pressed —
    // that blocked toggles whenever the left button was still down).
    if (event.button === 2 && !field[y][x].opened) {
      field = field.slice()
      field[y] = field[y].slice()
      const nextCell = {
        ...cell,
        flag: marks ? !cell.mark && !cell.flag : !cell.flag,
        mark: marks && cell.flag,
      }
      field[y][x] = nextCell
      flags = cell.flag === nextCell.flag ? flags : flags + (nextCell.flag ? -1 : 1)
    }
    if (ctrlFlag) {
      event.preventDefault()
      const r = toggleFlagAt(game, marks, x, y)
      if (!r) return null
      return {
        game: {
          ...r.game,
          pressed: false,
          pressedX: x,
          pressedY: y,
          group: false,
        },
      }
    }
    const chordGroup =
      event.shiftKey ||
      event.buttons > 2 ||
      event.button === 1 ||
      (event.button === 0 && openable(game, x, y))
    return {
      game: {
        ...game,
        field,
        pressed: event.button !== 2,
        pressedX: x,
        pressedY: y,
        group: chordGroup,
        flags,
      },
    }
  },
  move: ({ x, y }) => ({ game }) => {
    if (x === game.pressedX && y === game.pressedY) return null
    return {
      game: {
        ...game,
        pressedX: x,
        pressedY: y,
      },
    }
  },
  up: () => ({ game }, actions) => {
    window.removeEventListener('mouseup', actions.up)
    const { width, height, pressedX, pressedY } = game
    if (game.win || game.lose || pressedX < 0 || pressedY < 0) {
      if (!game.pressed && !game.group) return null
      return { game: { ...game, pressed: false, group: false } }
    }
    const field = game.field.map((row) => row.map((c) => ({ ...c })))
    const outBounds = (x, y) => x < 0 || y < 0 || x >= width || y >= height
    const calcNear = (x, y, fn) => {
      let num = 0
      for (let offsetX = -1; offsetX <= 1; offsetX++) {
        for (let offsetY = -1; offsetY <= 1; offsetY++) {
          if (!outBounds(offsetX + x, offsetY + y)) {
            num += fn(field[offsetY + y][offsetX + x]) ? 1 : 0
          }
        }
      }
      return num
    }

    let { left, lose } = game
    const reveal = (x, y) => {
      if (outBounds(x, y)) return 0
      const c = field[y][x]
      if (c.opened || c.flag) return 0
      c.opened = true
      c.num = calcNear(x, y, (i) => i.mined)
      if (c.mined) lose = true
      if (c.num) return 1
      return revealRange(x, y, reveal) + 1
    }

    const begin = game.start < 0
    if (begin) {
      for (let i = height * width - 2; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        const a = field[Math.floor(i / width)][i % width]
        const b = field[Math.floor(j / width)][j % width]
        const tmp = a.mined
        a.mined = b.mined
        b.mined = tmp
      }

      const a = field[height - 1][width - 1]
      const b = field[pressedY][pressedX]
      const tmp = a.mined
      a.mined = b.mined
      b.mined = tmp

      // eslint-disable-next-line no-param-reassign
      game.raf = requestAnimationFrame(actions.tick)

      window.gtag('event', 'start', {
        event_category: 'game',
        event_label: game.level,
      })
    }

    const cell = field[pressedY][pressedX]

    if (game.group) {
      if (cell.opened) {
        const flags = calcNear(pressedX, pressedY, (c) => c.flag)
        if (flags === cell.num) {
          left -= revealRange(pressedX, pressedY, reveal)
        }
      }
    } else if (game.pressed) {
      left -= reveal(pressedX, pressedY)
    }

    const win = left <= 0
    if (win || lose) {
      cancelAnimationFrame(game.raf)
      if (!lose) actions.win()
      window.gtag('event', lose ? 'lose' : 'win', {
        event_category: 'game',
        event_label: game.level,
        value: game.time,
      })
    }

    return {
      game: {
        ...game,
        left,
        field,
        pressed: false,
        group: false,
        start: begin ? performance.now() : game.start,
        time: begin ? 1 : game.time,
        lose,
        win,
        touch: false,
      },
    }
  },
  tick: (now) => ({ game }, actions) => {
    // eslint-disable-next-line no-param-reassign
    game.raf = requestAnimationFrame(actions.tick)
    const next = Math.floor((now - game.start) / 1000) + 1
    if (game.time === next) return null
    return { game: { ...game, time: next } }
  },
  win: () => ({ game }) => {
    if (game.level === 'custom') return null
    const time = parseInt(localStorage.getItem(game.level), 10)
    if (time && time <= game.time) return null
    return {
      winner: {
        name: 'Anonymous',
        time: game.time,
        level: game.level,
      },
    }
  },
  changeWinner: (payload) => ({ winner }) => ({
    winner: { ...winner, ...payload },
  }),
  submitBestTime: (event) => ({ winner }, actions) => {
    event.preventDefault()
    localStorage.setItem(winner.level, winner.time)
    localStorage.setItem(`${winner.level}Name`, winner.name.trim())
    actions.toggleBestTime()
    return { winner: null }
  },
  toggleBestTime: () => ({ bestTime }) => ({
    menu: null,
    bestTime: bestTime
      ? null
      : {
          levels: levelNames.map((level) => ({
            level,
            name: localStorage.getItem(`${level}Name`) || 'Anonymous',
            time: Math.min(999, parseInt(localStorage.getItem(level), 10) || 0) || 999,
          })),
        },
  }),
  resetBestTime: () => ({
    bestTime: {
      levels: levelNames.map((level) => ({
        level,
        name: localStorage.removeItem(`${level}Name`) || 'Anonymous',
        time: localStorage.removeItem(level) || 999,
      })),
    },
  }),
  toggleCustomField: () => ({ game, customField }) => ({
    menu: null,
    customField: customField
      ? null
      : {
          width: String(game.width),
          height: String(game.height),
          mines: String(game.mines),
        },
  }),
  changeCustomField: (payload) => ({ customField }) => ({
    customField: { ...customField, ...payload },
  }),
  submitCustomField: (event) => ({ game, customField }, actions) => {
    event.preventDefault()
    actions.reset(customLevel(customField, game))
    return { customField: null }
  },
  toggleMarks: () => (state) => {
    const marks = !state.marks
    localStorage.setItem('marks', marks ? 'yes' : '')
    if (!marks && state.game) {
      let changed = false
      const field = state.game.field.map((row) =>
        row.map((c) => {
          if (c.mark) {
            changed = true
            return { ...c, mark: false }
          }
          return c
        }),
      )
      if (changed) {
        return { menu: null, marks, game: { ...state.game, field } }
      }
    }
    return {
      menu: null,
      marks,
    }
  },
  setRegion: (region) => () => {
    if (!REGION_ASSETS[region]) return null
    if (canUseDom) localStorage.setItem('region', region)
    return { menu: null, region }
  },
  toggleMenu: (event) => ({ menu }) => {
    const { name } = event.currentTarget.dataset
    if (event.type === 'click') {
      return { menu: menu && menu[name] ? null : { [name]: true } }
    }
    if (event.type === 'mouseenter') {
      return menu ? { menu: { [name]: true } } : null
    }
    if (event.type === 'focusout' && !event.currentTarget.contains(event.relatedTarget)) {
      return { menu: null }
    }
    return null
  },
}

export const view = (state, actions) => {
  if (state.destroy) return null
  return (
    <main oncontextmenu={prevent} ondragstart={prevent} oncreate={actions.init}>
      <header class="site-header">
        <div class="page-header-brand">
          <img class="page-logo" src="/logo.png" alt="" width="112" height="112" decoding="async" />
          <Title key="title" />
        </div>
      </header>
      <div class="page-game-shell">
        {state.game && (
          <div class="page-game-shell-inner">
            <Game
              key={`${state.game.level}-${state.game.width}x${state.game.height}-${state.game.mines}`}
              state={state}
              actions={actions}
            />
          </div>
        )}
      </div>
      <section class="page-instructions" aria-label="Instructions">
        <h2 class="page-instructions-title">Instructions</h2>
        <div class="page-instructions-columns">
          <div class="page-instructions-block">
            <h3 class="page-instructions-subtitle">Desktop</h3>
            <ul class="page-instructions-list">
              <li>Left-click an empty square to reveal it.</li>
              <li>Right-click (or Ctrl+click) an empty square to flag it.</li>
              <li>Middle-click (or left+right click) a number to reveal its adjacent squares.</li>
              <li>
                Press the space bar while hovering over a square to flag it or reveal its adjacent
                squares.
              </li>
              <li>Press F2 or N, or click the smiley face, to start a new game.</li>
            </ul>
          </div>
          <div class="page-instructions-block">
            <h3 class="page-instructions-subtitle">Mobile</h3>
            <ul class="page-instructions-list">
              <li>Tap an empty square to reveal it.</li>
              <li>Long-press an empty square to flag it.</li>
              <li>Tap a number to reveal its adjacent squares.</li>
            </ul>
          </div>
        </div>
      </section>
      <div class="page-bmc-bottom">
        <div class="page-bmc">
          <div class="bmc-btn-container">
            <a
              class="bmc-btn"
              href="https://buymeacoffee.com/trumpsweeper"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://cdn.buymeacoffee.com/buttons/v2/default-yellow.png"
                alt="Buy me a coffee"
                width="140"
                height="39"
                loading="lazy"
                decoding="async"
              />
            </a>
          </div>
        </div>
      </div>
      {state.winner && <Winner key="b" state={state.winner} actions={actions} />}
      {state.bestTime && <BestTime key="c" state={state.bestTime} actions={actions} />}
      {state.customField && <CustomField key="d" state={state.customField} actions={actions} />}
    </main>
  )
}
